/**
 * In-person sessions.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from '@/services/api-client';
import type {
  Session,
  CreateSessionDto,
  UpdateSessionDto,
  SessionWithParticipants,
  SessionParticipant,
  SessionGroup,
  SessionGroupsResponse,
  ListSessionGroupsParams,
  CancelSessionDto,
  CancelGroupResult,
  UpdateSessionGroupDto,
  SessionListItem,
  SessionsResponse,
  UpdateParticipantAttendanceDto,
  TutorAvailabilityResult,
  StudentAvailabilityResult,
  ClassroomCapacityResult,
  RecurrencePreviewInput,
  RecurrencePreview,
  SessionBulkJob,
  QueuedSessionsBulkJob,
} from '@/types/inperson/session.types';
import { toLocalDateString } from '@/utils/format-date-utils';

/** Query string for `GET /sessions` list and `GET /sessions/export`. */
const buildSessionQueryParams = (params?: {
  page?: number;
  pageSize?: number;
  types?: string[];
  statuses?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  establishmentId?: string;
  subjectId?: string;
  levelId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  recurrenceType?: string;
}): URLSearchParams => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.types && params.types.length > 0) {
    params.types.forEach((type) => queryParams.append('types', type));
  }
  if (params?.statuses && params.statuses.length > 0) {
    params.statuses.forEach((status) => queryParams.append('statuses', status.toUpperCase()));
  }
  if (params?.dateFrom) {
    queryParams.append('dateFrom', toLocalDateString(params.dateFrom));
  }
  if (params?.dateTo) {
    queryParams.append('dateTo', toLocalDateString(params.dateTo));
  }
  if (params?.establishmentId) {
    queryParams.append('establishmentId', params.establishmentId);
  }
  if (params?.subjectId) {
    queryParams.append('subjectId', params.subjectId);
  }
  if (params?.levelId) {
    queryParams.append('levelId', params.levelId);
  }
  if (params?.sortBy) {
    queryParams.append('sortBy', params.sortBy);
  }
  if (params?.sortOrder) {
    queryParams.append('sortOrder', params.sortOrder);
  }
  if (params?.search) {
    queryParams.append('search', params.search);
  }
  if (params?.recurrenceType) {
    queryParams.append('recurrenceType', params.recurrenceType);
  }
  return queryParams;
};

/** `GET /sessions` — paginated in-person sessions. */
export const getSessions = async (params?: {
  page?: number;
  pageSize?: number;
  types?: string[];
  statuses?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  establishmentId?: string;
  subjectId?: string;
  levelId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  recurrenceType?: string;
}): Promise<SessionsResponse> => {
  const queryParams = buildSessionQueryParams(params);
  const queryString = queryParams.toString();
  const url = queryString ? `/sessions?${queryString}` : '/sessions';

  const response = await apiClient.get<{
    data: Array<
      Omit<SessionListItem, 'sessionDate' | 'status'> & { sessionDate: string; status: string }
    >;
    total: number;
    page: number;
    pageSize: number;
  }>(url);

  return {
    data: response.data.data.map((session) => {
      const [year, month, day] = session.sessionDate.split('-').map(Number);
      const sessionDate = new Date(year, month - 1, day);

      return {
        ...session,
        sessionDate,
        status: session.status as SessionListItem['status'],
      };
    }),
    total: response.data.total,
    page: response.data.page,
    pageSize: response.data.pageSize,
  };
};

/** `GET /sessions/:id` */
export const getSession = async (id: string): Promise<SessionWithParticipants> => {
  const sessionRes = await apiClient.get<
    Omit<SessionWithParticipants, 'sessionDate'> & { sessionDate: string }
  >(`/sessions/${id}`);

  const [year, month, day] = sessionRes.data.sessionDate.split('-').map(Number);
  const sessionDate = new Date(year, month - 1, day);

  return {
    ...sessionRes.data,
    sessionDate,
    participants: sessionRes.data.participants ?? [],
  };
};

/** `POST /sessions` */
export const createSession = async (dto: CreateSessionDto): Promise<Session | Session[]> => {
  const { data } = await apiClient.post('/sessions', dto);
  return data;
};

/** `PATCH /sessions/:id` */
export const updateSession = async (id: string, dto: UpdateSessionDto): Promise<Session> => {
  const { data } = await apiClient.patch(`/sessions/${id}`, dto);
  return data;
};

/** `POST /sessions/:id/cancel` */
export const cancelSession = async (
  id: string,
  dto: { reasonCode: string; reasonDetail?: string; dematchMode?: string },
): Promise<Session> => {
  const { data } = await apiClient.post<Session>(`/sessions/${id}/cancel`, dto);
  return data;
};

/** `PATCH /sessions/:sessionId/participants/:participantId` */
export const updateParticipantAttendance = async (
  sessionId: string,
  participantId: string,
  dto: UpdateParticipantAttendanceDto,
): Promise<SessionParticipant> => {
  const { data } = await apiClient.patch(
    `/sessions/${sessionId}/participants/${participantId}`,
    dto,
  );
  return data;
};

/** Trigger a browser download for an XLSX blob. */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /sessions/check-tutor-availability` — overlap at the given date/time slot. */
export const checkTutorAvailability = async (params: {
  tutorId: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  excludeSessionId?: string;
  excludeSessionGroupId?: string;
}): Promise<TutorAvailabilityResult> => {
  const query = new URLSearchParams({
    tutorId: params.tutorId,
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    endTime: params.endTime,
  });
  if (params.excludeSessionId) {
    query.append('excludeSessionId', params.excludeSessionId);
  }
  if (params.excludeSessionGroupId) {
    query.append('excludeSessionGroupId', params.excludeSessionGroupId);
  }
  const { data } = await apiClient.get<TutorAvailabilityResult>(
    `/sessions/check-tutor-availability?${query.toString()}`,
  );
  return data;
};

/** `GET /sessions/check-student-availability` — overlap at the given date/time slot. */
export const checkStudentAvailability = async (params: {
  studentId: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  excludeSessionId?: string;
  excludeSessionGroupId?: string;
}): Promise<StudentAvailabilityResult> => {
  const query = new URLSearchParams({
    studentId: params.studentId,
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    endTime: params.endTime,
  });
  if (params.excludeSessionId) {
    query.append('excludeSessionId', params.excludeSessionId);
  }
  if (params.excludeSessionGroupId) {
    query.append('excludeSessionGroupId', params.excludeSessionGroupId);
  }
  const { data } = await apiClient.get<StudentAvailabilityResult>(
    `/sessions/check-student-availability?${query.toString()}`,
  );
  return data;
};

/** `GET /sessions/check-room-capacity` — whether the classroom fits extra participants. */
export const checkClassroomCapacity = async (params: {
  roomId: string;
  participantsToAdd: number;
  sessionDate: string;
  startTime: string;
  endTime: string;
  excludeSessionId?: string;
}): Promise<ClassroomCapacityResult> => {
  const query = new URLSearchParams({
    roomId: params.roomId,
    participantsToAdd: String(params.participantsToAdd),
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    endTime: params.endTime,
  });
  if (params.excludeSessionId) {
    query.append('excludeSessionId', params.excludeSessionId);
  }
  const { data } = await apiClient.get<ClassroomCapacityResult>(
    `/sessions/check-room-capacity?${query.toString()}`,
  );
  return data;
};

/**
 * How many sessions a recurrence would create, computed by the same expansion as the real create
 * so the figure shown to the user cannot drift from what lands in the database.
 */
export const previewRecurrence = async (
  input: RecurrencePreviewInput,
): Promise<RecurrencePreview> => {
  const { data } = await apiClient.post<RecurrencePreview>('/sessions/recurrence-preview', input);
  return data;
};

/** `POST /sessions/bulk-async` — queue a bulk create; sessions are still being written. */
export const createSessionsBulkAsync = async (
  sessions: CreateSessionDto[],
): Promise<QueuedSessionsBulkJob> => {
  const { data } = await apiClient.post<QueuedSessionsBulkJob>('/sessions/bulk-async', {
    sessions,
  });
  return data;
};

/** `GET /sessions/bulk-jobs/:jobId` */
const getSessionsBulkJob = async (jobId: string): Promise<SessionBulkJob> => {
  const { data } = await apiClient.get<SessionBulkJob>(`/sessions/bulk-jobs/${jobId}`);
  return data;
};

/**
 * Poll intervals, in order, then the last value repeats. Front-loaded so a small create costs
 * roughly one extra round-trip and still feels synchronous, while a school-year series settles
 * into a slower cadence.
 */
const BULK_JOB_POLL_INTERVALS_MS = [250, 500, 1000, 1500];
/** Stop polling eventually; the server keeps going and the session list is the source of truth. */
const BULK_JOB_MAX_WAIT_MS = 15 * 60 * 1000;

/**
 * Follow a queued bulk job until it settles, reporting every poll through `onProgress`.
 *
 * Kept separate from queuing on purpose: everything that can still be corrected in the form —
 * room capacity, participant rules, the size ceiling — fails while queuing, so the form can catch
 * it. Once a job is accepted nothing is correctable, and following it belongs to whatever outlives
 * the form.
 */
export const pollSessionsBulkJob = async (
  jobId: string,
  onProgress?: (job: SessionBulkJob) => void,
): Promise<SessionBulkJob> => {
  const deadline = Date.now() + BULK_JOB_MAX_WAIT_MS;

  for (let attempt = 0; ; attempt++) {
    const wait =
      BULK_JOB_POLL_INTERVALS_MS[Math.min(attempt, BULK_JOB_POLL_INTERVALS_MS.length - 1)];
    await new Promise((resolve) => setTimeout(resolve, wait));
    const job = await getSessionsBulkJob(jobId);
    onProgress?.(job);
    if (job.status === 'completed' || job.status === 'failed') return job;
    if (Date.now() > deadline) return job;
  }
};

/** `GET /sessions/export` — same filters as {@link getSessions}, without page/pageSize. */
export const exportSessionsExcel = async (params?: {
  types?: string[];
  statuses?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  establishmentId?: string;
  subjectId?: string;
  levelId?: string;
  search?: string;
  recurrenceType?: string;
}): Promise<void> => {
  const queryParams = buildSessionQueryParams(params);

  const response = await apiClient.get(`/sessions/export?${queryParams.toString()}`, {
    responseType: 'blob',
  });
  triggerBlobDownload(
    new Blob([response.data], { type: XLSX_MIME }),
    `sessions-${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
};

/** Scalar `GET /session-groups` query keys (arrays are appended separately). */
const GROUP_SCALAR_QUERY_KEYS = [
  'dateFrom',
  'dateTo',
  'establishmentId',
  'schoolYearId',
  'subjectId',
  'levelId',
  'tutorId',
  'animatorUserId',
  'search',
  'seriesId',
  'recurrenceType',
  'sortOrder',
] as const satisfies readonly (keyof ListSessionGroupsParams)[];

/** Query string for session-group list/export; omit paging when `includePaging` is false. */
const buildGroupQueryParams = (
  params?: ListSessionGroupsParams,
  includePaging = true,
): URLSearchParams => {
  const q = new URLSearchParams();
  if (includePaging && params?.page) q.append('page', String(params.page));
  if (includePaging && params?.pageSize) q.append('pageSize', String(params.pageSize));
  for (const key of GROUP_SCALAR_QUERY_KEYS) {
    const value = params?.[key];
    if (value) q.append(key, value);
  }
  params?.statuses?.forEach((s) => q.append('statuses', s));
  params?.types?.forEach((t) => q.append('types', t));
  return q;
};

/** `GET /session-groups` — paginated in-person groups. */
export const getSessionGroups = async (
  params?: ListSessionGroupsParams,
): Promise<SessionGroupsResponse> => {
  const qs = buildGroupQueryParams(params).toString();
  const { data } = await apiClient.get<SessionGroupsResponse>(
    qs ? `/session-groups?${qs}` : '/session-groups',
  );
  return data;
};

/** `GET /session-groups/:id` */
export const getSessionGroup = async (id: string): Promise<SessionGroup> => {
  const { data } = await apiClient.get<SessionGroup>(`/session-groups/${id}`);
  return data;
};

/** `POST /session-groups/:id/cancel` */
export const cancelSessionGroup = async (
  id: string,
  dto: CancelSessionDto,
): Promise<CancelGroupResult> => {
  const { data } = await apiClient.post<CancelGroupResult>(`/session-groups/${id}/cancel`, dto);
  return data;
};

/** `PATCH /session-groups/:id` */
export const updateSessionGroup = async (
  id: string,
  dto: UpdateSessionGroupDto,
): Promise<SessionGroup> => {
  const { data } = await apiClient.patch<SessionGroup>(`/session-groups/${id}`, dto);
  return data;
};

/** `GET /session-groups/export` — same filters as {@link getSessionGroups}, without page/pageSize. */
export const exportSessionGroupsExcel = async (
  params?: Omit<ListSessionGroupsParams, 'page' | 'pageSize'>,
): Promise<void> => {
  const qs = buildGroupQueryParams(params, false).toString();
  const response = await apiClient.get(
    qs ? `/session-groups/export?${qs}` : '/session-groups/export',
    { responseType: 'blob' },
  );
  triggerBlobDownload(
    new Blob([response.data], { type: XLSX_MIME }),
    `sessions-${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
};
